import { useQuery } from '@tanstack/react-query'
import type { ColumnDef } from '@tanstack/react-table'
import { useMemo, useState } from 'react'
import { useTranslate } from 'shared/lib/i18n'
import type { RouterOutputs } from 'shared/api'
import { trpc } from 'shared/api'
import { DataTable } from 'shared/components/data-table/data-table'
import { PageFrame } from 'shared/components/page-frame'
import { SectionTitle } from 'shared/components/section'
import { Badge } from 'shared/components/ui/badge'
import { Input } from 'shared/components/ui/input'
import { Label } from 'shared/components/ui/label'
import { Tooltip, TooltipContent, TooltipTrigger } from 'shared/components/ui/tooltip'
import { useDataTable } from 'shared/hooks/use-data-table'
import { formatDayTime } from 'shared/lib/utils/date'

/**
 * «Реконсиляция» — the two queues the system keeps for a person (spec §12, user
 * stories 53–55, ticket 23), each a table. The **reconciliation queue** holds every
 * unmatched `UNEXPECTED_PARCEL` and every `CANCELLED_PARCEL_ARRIVED` (a Parcel the
 * partner had cancelled that physically arrived and was received, ADR-0022); each
 * already has its partner, and a person works it with them — nothing to press here,
 * what to do is on the kind's hover. The **stale expectations** are Parcels still
 * expected, old, and on no open Manifest — a list to chase, older than the days asked;
 * the system never changes their stage (a never-arriving Parcel ends only by the
 * partner's cancel).
 */

type QueueItem = RouterOutputs['reconciliation']['Queue'][number]
type StaleItem = RouterOutputs['expectations']['Stale'][number]

export function Reconciliation() {
	return (
		<PageFrame title='nav.reconciliation'>
			<div className='flex flex-col gap-8'>
				<ReconciliationQueue />
				<StaleExpectations />
			</div>
		</PageFrame>
	)
}

function ReconciliationQueue() {
	const { t } = useTranslate()
	const query = useQuery(trpc.reconciliation.Queue.queryOptions())
	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle title={t('reconciliation.queueTitle')} count={query.data?.length} hint={t('reconciliation.queueExplain')} />
			{query.isError ? (
				<p className='text-sm text-destructive'>{query.error.message}</p>
			) : query.isLoading ? (
				<p className='text-sm text-muted-foreground'>{t('reconciliation.loading')}</p>
			) : (
				<QueueTable items={query.data ?? []} />
			)}
		</section>
	)
}

/** The hint on a queue item's kind: what a person does with it. */
const HINT_KEY = {
	UNEXPECTED_PARCEL: 'reconciliation.unexpectedHint',
	CANCELLED_PARCEL_ARRIVED: 'reconciliation.cancelledArrivedHint',
} as const

/** The reconciliation queue as one table; a function of its items, nothing to press. */
export function QueueTable({ items }: { items: QueueItem[] }) {
	const { t } = useTranslate()
	const columns = useMemo<ColumnDef<QueueItem>[]>(
		() => [
			{
				id: 'kind',
				header: t('reconciliation.colKind'),
				cell: ({ row: { original: q } }) => (
					<Tooltip>
						<TooltipTrigger asChild>
							<Badge variant='outline' data-testid='queue-item-kind' data-hint={HINT_KEY[q.kind]}>
								{t(`reconciliationKind.${q.kind}`)}
							</Badge>
						</TooltipTrigger>
						<TooltipContent>{t(HINT_KEY[q.kind])}</TooltipContent>
					</Tooltip>
				),
			},
			{
				id: 'code',
				header: t('reconciliation.colCode'),
				cell: ({ row: { original: q } }) => <span className='font-mono'>{q.scannedCode}</span>,
			},
			{ id: 'point', header: t('reconciliation.atPoint'), cell: ({ row: { original: q } }) => q.pointCode || '—' },
			{
				id: 'manifest',
				header: t('reconciliation.atManifest'),
				cell: ({ row: { original: q } }) => (
					<span className='font-mono' data-testid='queue-item-manifest' data-manifest-id={q.manifestId}>
						{q.dispatchRef || '—'}
					</span>
				),
			},
			{
				id: 'at',
				header: t('reconciliation.colAt'),
				cell: ({ row: { original: q } }) => (
					<span className='text-muted-foreground tabular-nums'>{formatDayTime(q.at)}</span>
				),
			},
		],
		[t],
	)
	const { table } = useDataTable({
		data: items,
		columns,
		rowCount: items.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		autoResetPageIndex: false,
		getRowId: (q) => q.discrepancyId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})
	return (
		<DataTable
			table={table}
			withPagination={false}
			emptyText={t('reconciliation.queueEmpty')}
			row={{
				attributes: (q) => ({
					'data-testid': `queue-item-${q.discrepancyId}`,
					'data-kind': q.kind,
					'data-parcel-id': q.parcelId,
				}),
			}}
		/>
	)
}

const DAY_MS = 24 * 60 * 60 * 1000

function StaleExpectations() {
	const { t } = useTranslate()
	const [olderThanDays, setOlderThanDays] = useState<number>(7)
	const query = useQuery(trpc.expectations.Stale.queryOptions({ olderThanDays }))
	const items = useMemo(() => query.data ?? [], [query.data])

	const columns = useMemo<ColumnDef<StaleItem>[]>(
		() => [
			{
				id: 'code',
				header: t('reconciliation.colCode'),
				cell: ({ row: { original: s } }) => <span className='font-mono'>{s.trackingCode}</span>,
			},
			{ id: 'partner', header: t('reconciliation.colPartner'), cell: ({ row: { original: s } }) => s.partnerName },
			{
				id: 'recipient',
				header: t('reconciliation.colRecipient'),
				cell: ({ row: { original: s } }) => s.recipient.name || s.recipient.clientRef,
			},
			{ id: 'point', header: t('reconciliation.atPoint'), cell: ({ row: { original: s } }) => s.destinationPointCode },
			{
				id: 'age',
				header: t('reconciliation.ageDays'),
				meta: { headClassName: 'text-right', cellClassName: 'text-right tabular-nums' },
				cell: ({ row: { original: s } }) => Math.floor(s.ageMs / DAY_MS),
			},
		],
		[t],
	)
	const { table } = useDataTable({
		data: items,
		columns,
		rowCount: items.length,
		manualPagination: false,
		manualSorting: false,
		manualFiltering: false,
		enableRowSelection: false,
		autoResetPageIndex: false,
		getRowId: (s) => s.parcelId,
		initialState: { pagination: { pageIndex: 0, pageSize: 1000 } },
	})

	return (
		<section className='flex flex-col gap-2'>
			<SectionTitle
				title={t('reconciliation.staleTitle')}
				count={query.data?.length}
				hint={t('reconciliation.staleExplain')}
				aside={
					<div className='flex items-center gap-2'>
						<Label htmlFor='stale-older-than' className='text-muted-foreground'>
							{t('reconciliation.olderThanDays')}
						</Label>
						<Input
							id='stale-older-than'
							className='h-8 w-20'
							type='number'
							min={0}
							value={olderThanDays}
							onChange={(e) => setOlderThanDays(Math.max(0, Number(e.target.value) || 0))}
						/>
					</div>
				}
			/>
			{query.isError ? (
				<p className='text-sm text-destructive'>{query.error.message}</p>
			) : query.isLoading ? (
				<p className='text-sm text-muted-foreground'>{t('reconciliation.loading')}</p>
			) : (
				<DataTable table={table} withPagination={false} emptyText={t('reconciliation.staleEmpty')} />
			)}
		</section>
	)
}
